import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const postCommentApi = createApi({
  reducerPath: "postCommentApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Comment", "Replies"],
  endpoints: (build) => ({
    getPostCommentsAll: build.query<any, any>({
      query: (params) => ({
        url: "/comments",
        params,
      }),
    }),
    getPostComments: build.query<any, any>({
      query: (postUid) => ({
        url: `/comments/post/${postUid}`,
        method: "GET",
      }),
      transformResponse: (response: { data: any }) => response.data,
      providesTags: (result) =>
        Array.isArray(result)
          ? [
              ...result.map(({ uid }) => ({
                type: "Comment" as const,
                id: uid,
              })),
              { type: "Comment", id: "LIST" },
            ]
          : [{ type: "Comment", id: "LIST" }],
    }),
    getRepliesPostComments: build.query<any, { repliesUid: string; page: number; limit?: number }>({
      query: ({ repliesUid, page, limit = 20 }) => ({
        url: `/comments/${repliesUid}/replies?page=${page}&limit=${limit}&sort=desc`,
        method: "GET",
      }),
      providesTags: ({ repliesUid }) => [{ type: "Replies", id: repliesUid }],
    }),
    createPostComment: build.mutation<any, any>({
      query: (body) => ({
        url: "/comments",
        method: "POST",
        body,
      }),
      invalidatesTags: (arg) =>
        arg.replyToUid
          ? [{ type: "Replies", id: arg.replyToUid }]
          : [{ type: "Comment", id: "LIST" }],
    }),
    deletePostComment: build.mutation<any, any>({
      query: (commentUid) => ({
        url: `/comments/${commentUid}`,
        method: "DELETE",
      }),
    }),
    updatePostComment: build.mutation<any, { uid: string; content: string }>({
      query: ({ uid, content }) => ({
        url: `/comments/${uid}`,
        method: "PATCH",
        body: { content },
      }),
    }),
    likePostComment: build.mutation<any, any>({
      query: (body) => ({
        url: "/comment-reactions",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Comment", "Replies"],
    }),
    unlikePostComment: build.mutation<any, any>({
      query: (reactionUid) => ({
        url: `/comment-reactions/${reactionUid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Comment", "Replies"],
    }),
  }),
});

export const {
  useLikePostCommentMutation,
  useGetRepliesPostCommentsQuery,
  useLazyGetRepliesPostCommentsQuery,
  useGetPostCommentsAllQuery,
  useGetPostCommentsQuery,
  useCreatePostCommentMutation,
  useDeletePostCommentMutation,
  useUpdatePostCommentMutation,
  useUnlikePostCommentMutation,
} = postCommentApi;
